import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { PrintingService } from './printing.service';
import { TicketeraAgentService } from './ticketera-agent.service';
import { PrintTicket } from './printing.model';

/** Estado de la impresión automática en esta PC. */
export type StationRuntimeState = 'off' | 'standby' | 'running' | 'agent-missing' | 'error';

const STORAGE_KEY = 'coreshop.printStation.v1';
const POLL_MS = 5_000;
const MAX_BACKOFF_MS = 60_000;
const HEARTBEAT_MS = 60_000;

/** Espera cancelable: al detener la estación el bucle sale de inmediato y libera el bloqueo para otra pestaña. */
function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise(resolve => {
    const timer = setTimeout(resolve, ms);
    signal.addEventListener('abort', () => { clearTimeout(timer); resolve(); }, { once: true });
  });
}

/**
 * Impresión automática en la PC registrada como estación: pide al backend los tickets pendientes, los envía al micro
 * «ticketera» de esta PC y confirma cada uno (o informa el error para que se reintente). Corre en cualquier pantalla
 * del panel, no solo en la de Facturación.
 *
 * Con varias pestañas abiertas en la misma PC solo una imprime (Web Locks API); si se cierra, otra toma el relevo sin
 * duplicar tickets. La estación de esta PC se guarda en el navegador (no es un dato de la cuenta del usuario).
 */
@Injectable({ providedIn: 'root' })
export class PrintStationRuntimeService {
  private readonly printing = inject(PrintingService);
  private readonly agent = inject(TicketeraAgentService);

  readonly state = signal<StationRuntimeState>('off');
  readonly stationId = signal<number | null>(null);
  readonly printedCount = signal(0);
  readonly lastPrinted = signal<{ number: string | null; at: Date } | null>(null);
  readonly lastError = signal<string | null>(null);

  private companyId: number | null = null;
  private abort: AbortController | null = null;
  private lastHeartbeat = 0;
  private reportedPort: number | null = null;

  /** Estación configurada en esta PC para la tienda, o null. */
  boundStation(companyId: number): number | null {
    return this.readBindings()[String(companyId)] ?? null;
  }

  bind(companyId: number, stationId: number): void {
    const bindings = this.readBindings();
    bindings[String(companyId)] = stationId;
    this.writeBindings(bindings);
    this.start(companyId, true);
  }

  unbind(companyId: number): void {
    const bindings = this.readBindings();
    delete bindings[String(companyId)];
    this.writeBindings(bindings);
    if (this.companyId === companyId) this.stop();
  }

  /** Arranca la impresión si esta PC es estación de la tienda. Idempotente. */
  start(companyId: number, restart = false): void {
    const stationId = this.boundStation(companyId);
    if (!stationId) {
      if (this.companyId === companyId) this.stop();
      return;
    }
    if (!restart && this.abort && this.companyId === companyId && this.stationId() === stationId) return;
    this.stop();
    this.companyId = companyId;
    this.stationId.set(stationId);
    const abort = new AbortController();
    this.abort = abort;
    this.state.set('standby');
    const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
    if (locks) {
      locks.request(`coreshop-print-station-${companyId}`, { signal: abort.signal }, () => this.loop(companyId, stationId, abort.signal))
        .catch(() => undefined);
    } else {
      void this.loop(companyId, stationId, abort.signal);
    }
  }

  stop(): void {
    this.abort?.abort();
    this.abort = null;
    this.stationId.set(null);
    this.state.set('off');
  }

  private async loop(companyId: number, stationId: number, signal: AbortSignal): Promise<void> {
    let backoff = POLL_MS;
    while (!signal.aborted) {
      const ok = await this.tick(companyId, stationId);
      if (signal.aborted) break;
      backoff = ok ? POLL_MS : Math.min(backoff * 2, MAX_BACKOFF_MS);
      await sleep(backoff, signal);
    }
  }

  /** Una pasada: encontrar el micro, reclamar tickets, imprimirlos y confirmar. false = reintentar con espera. */
  private async tick(companyId: number, stationId: number): Promise<boolean> {
    const port = await this.agent.discover();
    if (!port) {
      this.state.set('agent-missing');
      return false;
    }
    await this.reportPort(companyId, stationId, port);
    let tickets: PrintTicket[];
    try {
      tickets = await firstValueFrom(this.printing.claim(companyId, stationId));
    } catch (err) {
      if (err instanceof HttpErrorResponse && err.error?.code === 'PRINT_STATION_NOT_FOUND') {
        // La estación se dio de baja desde otra PC: esta deja de imprimir.
        this.unbind(companyId);
        return false;
      }
      this.fail(err);
      return false;
    }
    this.state.set('running');
    for (const ticket of tickets) {
      if (ticket.jobId == null) continue;
      try {
        await this.agent.print(ticket.commands, port);
      } catch (err) {
        this.fail(err);
        await firstValueFrom(this.printing.markFailed(companyId, ticket.jobId, stationId, this.message(err))).catch(() => undefined);
        continue;
      }
      // Si la confirmación falla por red, el backend lo reintenta al vencer el reclamo (puede salir duplicado,
      // nunca perdido): se prefiere una copia de más a un cliente sin comprobante.
      await firstValueFrom(this.printing.markPrinted(companyId, ticket.jobId, stationId)).catch(err => this.fail(err));
      this.printedCount.update(n => n + 1);
      this.lastPrinted.set({ number: ticket.documentNumber, at: new Date() });
    }
    this.lastError.set(null);
    return true;
  }

  /**
   * Señal de vida con el puerto donde respondió el micro: el backend lo guarda si cambió (el micro salta de puerto si
   * el base está ocupado) para que la estación quede registrada con su puerto real.
   */
  private async reportPort(companyId: number, stationId: number, port: number): Promise<void> {
    const now = Date.now();
    if (port === this.reportedPort && now - this.lastHeartbeat < HEARTBEAT_MS) return;
    try {
      await firstValueFrom(this.printing.heartbeat(companyId, stationId, port));
      this.reportedPort = port;
      this.lastHeartbeat = now;
    } catch {
      // La señal de vida es informativa: el reclamo de tickets también la registra.
    }
  }

  private fail(err: unknown): void {
    this.lastError.set(this.message(err));
    this.state.set('error');
  }

  private message(err: unknown): string {
    if (err instanceof HttpErrorResponse) return err.error?.message ?? err.message;
    return err instanceof Error ? err.message : String(err);
  }

  private readBindings(): Record<string, number> {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : {};
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }

  private writeBindings(bindings: Record<string, number>): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(bindings));
    } catch {
      // Navegador sin almacenamiento (modo privado estricto): la estación dura solo esta sesión.
    }
  }
}
